import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import { Chip, FormControl, Popover, Slider, Tooltip } from "@mui/material";
import { RiOpenaiFill } from "react-icons/ri";
import { RiClaudeFill } from "react-icons/ri";
import Grid from "@mui/material/Grid2";
import { CustomButton } from "../../../../components/Button";
import TextInput from "../../../../components/TextInput";
import "./index.css";
import { IoIosClose } from "react-icons/io";
import ThreadAge from "../ThreadAge";
import {
  useCreateThreadMutation,
  useGenerateThreadMutation,
  useUpdateThreadMetadataMutation,
} from "../../../../services/chat";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { errorToast, successToast } from "../../../../utils/toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ReactComponent as DeepseekIcon } from "../../../../assets/images/deepseek.svg";
import { tokensCost } from "../../../../utils/functions";
import { tokenApi } from "../../../../services/token";
import { CiCircleQuestion } from "react-icons/ci";
import { setUser } from "../../../../rtkSlices/metaDataSlice";

type ThreadProps = {
  anchor?: HTMLElement | null;
  onClose?: () => void;
  setManageSummary: (value: string) => void;
  generatedSummary?: string;
  threadMetadata?: any;
  anchorEl?: HTMLDivElement | null;
};

const ManageThread: React.FC<ThreadProps> = ({
  anchor,
  onClose,
  setManageSummary,
  generatedSummary,
  threadMetadata,
  anchorEl,
}) => {
  const [anchorEl3, setAnchorEl3] = React.useState<
    HTMLButtonElement | HTMLDivElement | null
  >(anchorEl ? anchorEl : null);
  const [searchParams, setSearchParams] = useSearchParams();

  let threadType = anchor ? "new" : "edit";
  const theme = useTheme();
  const bgdefault = theme.palette.background.default;
  const [personalities, setPersonalities] = React.useState<any>([]);
  const [personalityInput, setPersonalityInput] = React.useState("");
  const [llm, setLlm] = React.useState("openai");
  const [createThread, createThreadResult] = useCreateThreadMutation();
  const [generateThread, generateThreadResult] = useGenerateThreadMutation();
  const [updateThreadMetadata, updateThreadMetadataResult] =
    useUpdateThreadMetadataMutation();
  const metaData = useSelector((state: any) => state.metaData);
  const user_id = metaData.userInfo["cognito:username"];
  const [temperature, setTemperature] = React.useState(0.7);
  const navigate = useNavigate();
  const {
    register,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      subject: "",
      summary: "",
      generated_name: "",
    },
  });
  const dispatch = useDispatch();
  const summaryWatch = watch("summary");
  const generated_name = watch("generated_name");

  React.useEffect(() => {
    if (anchorEl) {
      setAnchorEl3(anchorEl);
    }
  }, [anchorEl]);

  const personalityChips = (text: string, ind: number) => {
    return (
      <Chip
        key={ind}
        className="personality-chip"
        color="secondary"
        sx={{ backgroundColor: theme.palette.background.default }}
        label={text}
        onDelete={() => {
          const tempPersonalities = [...personalities];
          tempPersonalities.splice(tempPersonalities.indexOf(text), 1);
          setPersonalities(tempPersonalities);
        }}
        deleteIcon={<IoIosClose />}
      />
    );
  };

  const handleEnter = async (
    e: React.KeyboardEvent<HTMLDivElement> | string | null
  ) => {
    const keyboardEvent = e as React.KeyboardEvent<HTMLDivElement>;
    if (keyboardEvent && keyboardEvent.keyCode == 13) {
      if (personalities.includes(personalityInput)) {
        errorToast(`${personalityInput} personality already exists`);
        return;
      }
      const newPersonalities = [...personalities, personalityInput];
      if (
        newPersonalities &&
        newPersonalities.length &&
        newPersonalities.join().replaceAll(",", "").length > 100
      ) {
        errorToast(
          `You’ve hit the 100 character limit for thread personality.`
        );
        return;
      }
      setPersonalities([...personalities, personalityInput]);
      setPersonalityInput("");
    }
  };

  React.useEffect(() => {
    if (threadMetadata && threadMetadata.id) {
      setValue("generated_name", threadMetadata.generated_name);
      setPersonalities(
        threadMetadata.personality.length
          ? threadMetadata.personality.split(",")
          : []
      );
      setLlm(threadMetadata.llm_choice);
      setValue("subject", threadMetadata.subject);
      setValue("summary", threadMetadata.context);
      setTemperature(threadMetadata.temperature);
    }
  }, [JSON.stringify(threadMetadata)]);

  const onSubmitHandler = async () => {
    const { summary, generated_name, subject } = getValues();
    if (
      !summary ||
      !generated_name ||
      (threadType == "new" && !subject) ||
      !personalities.length
    ) {
      errorToast("Please fill in required fields");
      return;
    }
    let body: any = {
      conversation_id:
        threadType == "new" ? "" : searchParams.get("conversation_id") || "",
      user_id,
      summary,
      subject,
      generated_name,
      ai_llm: llm,
      personality: personalities.join(","),
      temperature,
    };
    if (threadType != "new") {
      delete body.ai_llm;
      delete body.summary;
      body.llm_choice = llm;
      body.context = summary;
    }
    const response: any =
      threadType == "new"
        ? await createThread(body)
        : await updateThreadMetadata(body);
    if (response.error) {
      errorToast(
        response.error.error ||
          (response.error.data && response.error.data.message)
      );
    } else {
      if (threadType == "new") {
        dispatch(tokenApi.util.invalidateTags(["tokenBalance"]));
      }
      if (response.data && response.data.id) {
        if (threadType == "new") {
          dispatch(setUser({ personalThread: false }));
          if (onClose) {
            onClose();
          }
          setAnchorEl3(null);
          navigate("/chat?conversation_id=" + response.data.conversation_id);
        } else {
          successToast("Thread metadata has been updated");
          if (onClose) {
            onClose();
          }
          setAnchorEl3(null);
        }
      }
    }
  };

  const generateThreadFunc = async () => {
    if (!getValues().subject) {
      errorToast("Enter subject first");
      return;
    }
    const body = {
      subject: getValues().subject,
      llm_choice: llm,
      user_id,
    };
    const response: any = await generateThread(body);
    if (response.error) {
      errorToast(
        response.error.error ||
          (response.error.data && response.error.data.message)
      );
    } else {
      dispatch(tokenApi.util.invalidateTags(["tokenBalance"]));
      if (response.data && response.data.thread_name) {
        setValue("generated_name", response.data.thread_name);
        setPersonalities(response.data.thread_personality);
        setValue("summary", response.data.thread_context);
      }
    }
  };
  const HallucinationSlider = styled(Slider)({
    "& .MuiSlider-thumb": {
      width: 3,
      height: 30,
      backgroundColor: "#2a945f",
      borderRadius: 4,
      marginLeft: temperature ? "1px" : "6px",
    },
  });

  const registerProps = { ...register("subject", { required: true }) };

  return (
    <>
      {!anchor && (
        <ThreadAge
          setManageSummary={setManageSummary}
          setAnchorEl3={setAnchorEl3}
          generatedSummary={generatedSummary}
        />
      )}
      <Popover
        open={anchorEl3 ? true : anchor ? true : false}
        anchorEl={anchorEl3 || anchor}
        onClose={() => {
          setAnchorEl3(null);
          if (onClose) {
            onClose();
          }
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        sx={{
          marginTop:
            threadType == "new" ? "-60px !important" : "0px !important",
          marginLeft: "10px",
          maxHeight: "620px !important",
        }}
      >
        <form>
          <div
            className="manage-thread-content"
            style={{ backgroundColor: bgdefault }}
          >
            <h4 className="thread-head">
              {threadType == "edit" ? "Thread Memory" : "Create a Thread"}
            </h4>
            <div className="subject-text">
              Thread LLM
              {threadType != "new" ? (
                <Tooltip
                  title={"Change LLMs by selecting in the dropdown list"}
                  placement="top"
                >
                  <div style={{ marginTop: "7px" }}>
                    <CiCircleQuestion size={16} />
                  </div>
                </Tooltip>
              ) : null}
            </div>
            {threadType == "new" ? (
              <div
                style={{
                  display: "flex",
                  gap: "4px",
                  alignItems: "center",
                  margin: "5px 0px",
                }}
              >
                <div style={{ fontStyle: "italic", fontSize: "12px" }}>
                  Select the LLM you would like to use for this thread:
                </div>
              </div>
            ) : null}
            <div
              style={{
                display: "flex",
                gap: "4px",
                alignItems: "center",
                margin: "10px 0px",
              }}
            >
              {llm == "openai" || threadType == "new" ? (
                <CustomButton
                  text={"OpenAI"}
                  color={theme.palette.primary.main}
                  submit={() => setLlm("openai")}
                  fontWeight="200"
                  fontSize="13px"
                  type="button"
                  bgcolor={
                    llm == "openai"
                      ? "rgba(42, 148, 95, 1)"
                      : "rgba(52, 180, 116, 0.2)"
                  }
                  width="20%"
                  borderRadius={"7px"}
                  pointerEvents={threadType == "new" ? "all" : "none"}
                  startIcon={
                    <RiOpenaiFill color={theme.palette.primary.main} />
                  }
                />
              ) : null}
              {llm == "claude" || threadType == "new" ? (
                <CustomButton
                  text={"Claude"}
                  color={theme.palette.primary.main}
                  submit={() => setLlm("claude")}
                  fontWeight="200"
                  fontSize="13px"
                  type="button"
                  bgcolor={
                    llm == "claude"
                      ? "rgba(42, 148, 95, 1)"
                      : "rgba(52, 180, 116, 0.2)"
                  }
                  width="20%"
                  borderRadius={"7px"}
                  pointerEvents={threadType == "new" ? "all" : "none"}
                  startIcon={
                    <RiClaudeFill color={theme.palette.primary.main} />
                  }
                />
              ) : null}
              {llm == "deepseek" || threadType == "new" ? (
                <CustomButton
                  text={"DeepSeek"}
                  color={theme.palette.primary.main}
                  submit={() => setLlm("deepseek")}
                  fontWeight="200"
                  fontSize="13px"
                  type="button"
                  bgcolor={
                    llm == "deepseek"
                      ? "rgba(42, 148, 95, 1)"
                      : "rgba(52, 180, 116, 0.2)"
                  }
                  width="20%"
                  borderRadius={"7px"}
                  startIcon={<DeepseekIcon className="deepseek" />}
                  pointerEvents={threadType == "new" ? "all" : "none"}
                />
              ) : null}
            </div>
            {threadType == "new" ? (
              <>
                <p className="subject-text">
                  What are you using this thread for?
                </p>
                <Grid
                  container
                  sx={{ width: "100%", alignItems: "center" }}
                  spacing={1}
                >
                  <Grid size={{ xs: 9, md: 9 }}>
                    <textarea
                      className="personality"
                      placeholder="Enter Subject *"
                      style={{
                        backgroundColor: bgdefault,
                        height: "55px",
                        border: errors.subject
                          ? "1px solid #e11c1cb3 !important"
                          : "1px solid #2a945f",
                      }}
                      {...registerProps}
                    ></textarea>
                  </Grid>
                  <Grid
                    size={{ xs: 3, md: 3 }}
                    display={"flex"}
                    justifyContent={"flex-start"}
                  >
                    <CustomButton
                      text={generateThreadResult.isLoading ? "" : "Generate"}
                      color={theme.palette.primary.main}
                      submit={generateThreadFunc}
                      fontSize="12px"
                      bgcolor="rgba(52, 180, 116, 0.5)"
                      width="100%"
                      height="55px"
                      fontWeight="200"
                      type="button"
                      disabled={generateThreadResult.isLoading}
                      endIcon={
                        metaData.tokenMetadata.actionsTokensCost &&
                        !generateThreadResult.isLoading
                          ? tokensCost(
                              metaData.tokenMetadata.actionsTokensCost
                                ?.generate_thread,
                              { fontSize: "13px" }
                            )
                          : undefined
                      }
                    />
                  </Grid>
                </Grid>
              </>
            ) : null}
            <Grid
              container
              sx={{ width: "100%", alignItems: "center", marginTop: "5px" }}
              spacing={1}
            >
              <div className="thread-name">
                <div>Thread Name</div>
                <div>{generated_name.length}/50 chars</div>
              </div>
              <Grid size={{ xs: 12, md: 12 }}>
                <FormControl variant="standard" fullWidth>
                  <TextInput
                    label="Thread Name"
                    id="thread-name"
                    placeholder="Thread name"
                    maxLength={50}
                    ariaLabel="threadName"
                    type="text"
                    shrink={watch("generated_name") ? true : undefined}
                    name="generated_name"
                    defaultValue=""
                    register={register}
                    required={false}
                  />
                </FormControl>
              </Grid>
            </Grid>
            <div className="thread-section">
              <div className="subject-text">
                <div>Thread Personality</div>
                <div>
                  {personalities.length
                    ? personalities.join("").replaceAll(",", "").length +
                      personalityInput.length
                    : personalityInput.length}
                  /100 chars
                </div>
              </div>
              <Grid
                container
                sx={{ width: "100%", alignItems: "center" }}
                spacing={1}
              >
                <Grid size={{ xs: 12, md: 12 }}>
                  <FormControl variant="standard" fullWidth>
                    <TextInput
                      label="Add new personality input"
                      id="new-personality-input"
                      placeholder="Add new personality input"
                      ariaLabel="newPersonalityInput"
                      type="text"
                      name="newPersonalityInput"
                      value={personalityInput}
                      handleChange={(e) => setPersonalityInput(e.target.value)}
                      handleKeyUp={handleEnter}
                    />
                  </FormControl>
                </Grid>
              </Grid>
              {personalities.length ? (
                <div className="personality-chips">
                  {personalities.map((personality: any, ind: number) => {
                    return personalityChips(personality, ind);
                  })}
                </div>
              ) : null}
            </div>
            <div className="thread-section">
              <div className="subject-text">
                <div>Thread Context</div>
                <div>{summaryWatch.length}/1600 chars</div>
              </div>
              <Grid
                container
                sx={{ width: "100%", alignItems: "center" }}
                spacing={1}
              >
                <Grid size={{ xs: 12, md: 12 }}>
                  <FormControl variant="standard" fullWidth>
                    <TextInput
                      label="Enter thread context"
                      id="summary"
                      placeholder="Enter thread context"
                      type="text"
                      maxLength={1600}
                      defaultValue=""
                      name="summary"
                      multiline={true}
                      shrink={watch("summary") ? true : undefined}
                      register={register}
                      required={false}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </div>
            <div className="thread-section">
              <div className="subject-text">
                <div>Hallucination Tolerance</div>
              </div>
              <HallucinationSlider
                aria-label="Temperature"
                defaultValue={temperature}
                valueLabelDisplay="auto"
                step={0.1}
                marks
                min={0}
                max={1}
                sx={{
                  backgroundColor: "#2a945f",
                  color: theme.palette.background.default,
                  marginTop: "4px",
                }}
                onChangeCommitted={(
                  event: React.SyntheticEvent | Event,
                  value: number | number[]
                ) => {
                  setTemperature(value as number);
                }}
              />
            </div>

            <div
              className="thread-age-btns"
              style={{
                justifyContent: threadType == "new" ? "moz-initial" : "center",
              }}
            >
              <div
                style={{
                  width: "50%",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <CustomButton
                  text={threadType == "new" ? "Cancel" : "Back to chat"}
                  color={
                    threadType == "new"
                      ? "rgba(180, 69, 52, 1)"
                      : "rgba(11, 43, 27, 1)"
                  }
                  border={
                    threadType == "new"
                      ? "1px solid rgba(180, 69, 52, 1)"
                      : "none"
                  }
                  bgcolor={
                    threadType == "new" ? bgdefault : "rgba(180, 69, 52, 1)"
                  }
                  submit={() => {
                    if (onClose) {
                      onClose();
                    }
                    setAnchorEl3(null);
                  }}
                  type="button"
                  fontWeight="600"
                  fontSize="13px"
                  width={threadType == "new" ? "100%" : "50%"}
                />
              </div>
              <div
                style={{
                  width: "50%",
                  display: "flex",
                  justifyContent: "flex-start",
                }}
              >
                <CustomButton
                  text={
                    threadType == "new"
                      ? createThreadResult.isLoading
                        ? ""
                        : "Create thread"
                      : updateThreadMetadataResult.isLoading
                      ? ""
                      : "Save changes"
                  }
                  color={
                    threadType == "new"
                      ? theme.palette.primary.main
                      : "rgba(11, 43, 27, 1)"
                  }
                  type="button"
                  submit={onSubmitHandler}
                  fontWeight="600"
                  fontSize="13px"
                  bgcolor="#2a945f"
                  width={threadType == "new" ? "100%" : "50%"}
                  endIcon={
                    threadType == "new" && !createThreadResult.isLoading
                      ? tokensCost(
                          metaData.tokenMetadata.actionsTokensCost
                            ?.create_thread,
                          { fontSize: "13px" }
                        )
                      : undefined
                  }
                />
              </div>
            </div>
          </div>
        </form>
      </Popover>
    </>
  );
};
export default ManageThread;
